import { useWeb3React } from '@web3-react/core'
import { PermitSignature } from 'hooks/usePermitAllowance'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useERC721Contract } from './useContract'
import { useSingleCallResult } from 'lib/hooks/multicall'
import { ContractTransaction, ethers } from 'ethers'
import { ApproveTransactionInfo, TransactionType } from 'state/transactions/types'
import { trace } from 'tracing/trace'
import { didUserReject } from 'utils/swapErrorToUserReadableMessage'
import { UserRejectedRequestError } from 'utils/errors'
import { useEthersWeb3Provider } from './useEthersProvider'
import NutsJSON from '../constants/Nuts.json'
import { MaxUint256 } from '@uniswap/sdk-core'

enum ApprovalState {
  PENDING,
  SYNCING,
  SYNCED,
}

export enum AllowanceState {
  LOADING,
  REQUIRED,
  ALLOWED,
}

interface AllowanceRequired {
  state: AllowanceState.REQUIRED
  isApprovalLoading: boolean
  isApprovalPending: boolean
  isRevocationPending: boolean
  approve: () => Promise<void>
  needsSetupApproval: boolean
}

export type Allowance =
  | { state: AllowanceState.LOADING }
  | {
    state: AllowanceState.ALLOWED
    permitSignature?: PermitSignature
  }
  | AllowanceRequired

function useERC721IsApprovedForAll(
  operator: string,
  owner?: string,
): {
  isApprovedForAll?: boolean
  isSyncing: boolean
} {
  const NUTS_ADDRESS = '0x88266f9eb705F5282a2507A9c418821a2AC9f8BD' // ugly hard coded
  const contract = useERC721Contract(NUTS_ADDRESS)
  const inputs = useMemo(() => [owner, operator], [owner, operator])

  // If there is no approvedForAll yet, re-check next observed block.
  // This guarantees that the useERC721IsApprovedForAll is marked isSyncing upon approval and updated upon being synced.
  const [blocksPerFetch, setBlocksPerFetch] = useState<1>()
  const { result, syncing: isSyncing } = useSingleCallResult(contract, 'isApprovedForAll', inputs, { blocksPerFetch }) as {
    result?: Awaited<ReturnType<NonNullable<typeof contract>['isApprovedForAll']>>
    syncing: boolean
  }


  const rawAmount = result?.toString() // convert to a string before using in a hook, to avoid spurious rerenders
  const approvedForAll = useMemo(
    () => (rawAmount == "false" ? false : rawAmount == "true" ? true : undefined),
    [rawAmount]
  )
  useEffect(() => setBlocksPerFetch(approvedForAll?.toString().startsWith("false") ? 1 : undefined), [approvedForAll])

  return useMemo(() => ({ isApprovedForAll: approvedForAll, isSyncing }), [approvedForAll, isSyncing])
}

function useUpdateERC721IsApprovedForAll(
  operator: string
): () => Promise<{ response: ContractTransaction ; info: ApproveTransactionInfo }> {
  const provider = useEthersWeb3Provider()
  const NUTS_ADDRESS = '0x88266f9eb705F5282a2507A9c418821a2AC9f8BD' // ugly hard coded

  return useCallback(
    () =>
      trace({ name: 'SetApprovalForAll', op: 'wallet.send_transaction' }, async (trace) => {
        try {
          if (!provider) throw new Error('missing provider')
          if (!operator) throw new Error('missing operator')

          const approved = true
          const response = await trace.child({ name: 'Send transaction', op: 'wallet.send_transaction' }, async (walletTrace) => {
            try {
              const signer = provider.getSigner()
              const contract = new ethers.Contract(NUTS_ADDRESS, NutsJSON.abi, signer)
              return await contract.connect(signer).setApprovalForAll(operator, approved)

            } catch (error) {
              console.error(error)
              if (didUserReject(error)) {
                walletTrace.setStatus('cancelled')
                throw new UserRejectedRequestError(`NUTS NFT setApprovalForAll: User rejected`)
              } else {
                throw error
              }
            }
          })

  
          return {
            response,
            info: {
              type: TransactionType.APPROVAL,
              tokenAddress: NUTS_ADDRESS,
              spender: operator,
              amount: MaxUint256.toString(), // ugly workaround: max allowance to say all NFTs approved 
            },
          }
        } catch (error: unknown) {
          console.error(error)
          if (error instanceof UserRejectedRequestError) {
            trace.setStatus('cancelled')
            throw error
          } else {
            throw new Error(`NUTS NFT setApprovalForAll: ${error instanceof Error ? error.message : error}`)
          }
        }

      }),
    [operator]
  )
}

export default function usePermitAllowanceNFT(
): Allowance {
  const { account } = useWeb3React()

  const NUTS404_ADDRESS = '0x25559f0abbaf2a928239d2f419181147cc2dad74' // ugly hard coded

  const { isApprovedForAll, isSyncing: isApprovalSyncing } = useERC721IsApprovedForAll(NUTS404_ADDRESS, account)

  const updateERC721IsApprovedForAll = useUpdateERC721IsApprovedForAll(NUTS404_ADDRESS)


  const isApproved = useMemo(() => {
    if (!isApprovedForAll) return false
    return isApprovedForAll
  }, [isApprovedForAll])


  // Marks approval as loading from the time it is submitted (pending), until it has confirmed and another block synced.
  // This avoids re-prompting the user for an already-submitted but not-yet-observed approval, by marking it loading
  // until it has been re-observed. It wll sync immediately, because confirmation fast-forwards the block number.
  const [approvalState, setApprovalState] = useState(ApprovalState.SYNCED)
  const isApprovalLoading = approvalState !== ApprovalState.SYNCED
  const isApprovalPending = false
  const isRevocationPending = false

  useEffect(() => {
    if (isApprovalPending) {
      setApprovalState(ApprovalState.PENDING)
    } else {
      setApprovalState((state) => {
        if (state === ApprovalState.PENDING && isApprovalSyncing) {
          return ApprovalState.SYNCING
        } else if (state === ApprovalState.SYNCING && !isApprovalSyncing) {
          return ApprovalState.SYNCED
        }
        return state
      })
    }
  }, [isApprovalPending, isApprovalSyncing])


  const addTransaction = useTransactionAdder()

  const approve = useCallback(async () => {
    const { response, info } = await updateERC721IsApprovedForAll()
    addTransaction(response, info)
  }, [addTransaction, updateERC721IsApprovedForAll])


  return useMemo(() => {

    if (isApprovedForAll == undefined) {
      return { state: AllowanceState.LOADING }

    } else if (!isApproved) {
      return {
        state: AllowanceState.REQUIRED,
        isApprovalLoading,
        isApprovalPending,
        isRevocationPending,
        approve,
        needsSetupApproval: true,
      }
    }

    return {
      state: AllowanceState.ALLOWED,
      permitSignature: undefined,
    }
  }, [
    approve,
    isApprovalLoading,
    isApprovalPending,
    isRevocationPending,
    isApproved,
    isApprovedForAll,
  ])
}
